import { useEffect } from 'react';
import WavoipWebphone from '@wavoip/wavoip-webphone';

const isProd = process.env.NODE_ENV === "production";

/**
 * Componente Wavoip Webphone - Widget oficial da Wavoip
 * 
 * Este componente renderiza o widget de telefone oficial da Wavoip,
 * que inclui UI completa, gerenciamento de chamadas, áudio e notificações.
 * 
 * @param {Object} props
 * @param {string} props.token - Token do dispositivo Wavoip
 * @param {Function} props.onCallStart - Callback quando chamada inicia
 * @param {Function} props.onCallEnd - Callback quando chamada termina
 * @param {Function} props.onError - Callback para erros
 */
const WavoipWidget = ({ 
  token,
  onCallStart,
  onCallEnd,
  onError
}) => {
  useEffect(() => {
    if (!token) {
      if (!isProd) {
        console.log('⚠️ [WAVOIP] Token não fornecido');
      }
      return;
    }

    if (!isProd) {
      console.log('🚀 [WAVOIP] Inicializando webphone com token:', token);
    }

    const initWebphone = async () => {
      try {
        // Solicitar permissões de mídia ANTES de inicializar o webphone
        try {
          if (!isProd) {
            console.log('🎤 [WAVOIP] Solicitando permissões de áudio...');
          }
          
          const stream = await navigator.mediaDevices.getUserMedia({ 
            audio: {
              echoCancellation: true,
              noiseSuppression: true,
              autoGainControl: true
            }, 
            video: false 
          });
          
          // Liberar o stream imediatamente - apenas queríamos a permissão
          stream.getTracks().forEach(track => track.stop());
          
          if (!isProd) {
            console.log('✅ [WAVOIP] Permissões de áudio concedidas');
          }
        } catch (mediaErr) {
          console.error('❌ [WAVOIP] Erro ao solicitar permissões de áudio:', mediaErr);
          alert('Permissão de microfone negada. Por favor, permita o acesso ao microfone para fazer chamadas.');
          return;
        }

        // Renderizar webphone com configurações
        WavoipWebphone.render({
        theme: "system", // Tema automático (light/dark)
        position: "bottom-right", // Posição do widget
        widget: {
          showWidgetButton: true, // Mostrar botão flutuante verde
          startOpen: false // Iniciar fechado
        },
        statusBar: {
          showNotificationsIcon: true, // Sino de notificações
          showSettingsIcon: true // Mostrar configurações para ajustar áudio se necessário
        },
        settingsMenu: {
          deviceMenu: {
            show: true, // Mostrar menu de dispositivos para debug
            showAddDevices: false, // Não permitir adicionar manualmente
            showEnableDevicesButton: true, // Permitir habilitar dispositivos
            showRemoveDevicesButton: false // Esconder botão remover
          }
        },
        // Configurações de áudio explícitas
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true
        }
      });

      if (!isProd) {
        console.log('✅ [WAVOIP] Webphone renderizado');
      }

      // Aguardar um momento para garantir que o webphone está pronto
      await new Promise(resolve => setTimeout(resolve, 100));

      // Adicionar dispositivo com persistência no localStorage
      if (window.wavoip?.device) {
        window.wavoip.device.add(token, true);
        
        if (!isProd) {
          console.log('✅ [WAVOIP] Dispositivo adicionado:', token);
          
          // Verificar se foi adicionado corretamente
          const devices = window.wavoip.device.get();
          console.log('📱 [WAVOIP] Dispositivos conectados:', devices);
        }
      } else {
        console.error('❌ [WAVOIP] window.wavoip.device não disponível!');
      }

      // Monitorar chamadas recebidas (offers) para abrir widget automaticamente
      let lastOfferCount = 0;
      const checkIncomingCalls = setInterval(() => {
        try {
          const offers = window.wavoip?.call?.getOffers();
          
          if (offers && offers.length > lastOfferCount) {
            // Nova chamada recebida!
            if (!isProd) {
              console.log('📞 [WAVOIP] CHAMADA RECEBIDA! Abrindo widget...', offers);
            }
            
            // Abrir widget automaticamente para mostrar chamada
            if (window.wavoip?.widget) {
              window.wavoip.widget.open();
            }

            lastOfferCount = offers.length;
          } else if (offers) {
            lastOfferCount = offers.length;
          }
        } catch (err) {
          if (!isProd) {
            console.error('❌ [WAVOIP] Erro ao verificar chamadas recebidas:', err);
          }
        }
      }, 1000); // Verificar a cada 1 segundo

      // Monitorar estado das chamadas (se callbacks fornecidos)
      let checkCallStateInterval;
      if (onCallStart || onCallEnd) {
        checkCallStateInterval = setInterval(() => {
          try {
            const activeCall = window.wavoip.call.getCallActive();
            const outgoingCall = window.wavoip.call.getCallOutgoing();
            const offers = window.wavoip.call.getOffers();

            // Chamada ativa iniciada
            if (activeCall && onCallStart) {
              if (!isProd) {
                console.log('📞 [WAVOIP] Chamada ativa:', activeCall.peer.phone);
              }
            }

            // Verificar se chamada terminou (sem chamada ativa)
            if (!activeCall && !outgoingCall && offers.length === 0 && onCallEnd) {
              // Chamada terminou
            }
          } catch (err) {
            if (!isProd) {
              console.error('❌ [WAVOIP] Erro ao verificar estado:', err);
            }
          }
        }, 2000); // Verificar a cada 2 segundos
      }

      // Cleanup dos intervalos
      return () => {
        clearInterval(checkIncomingCalls);
        if (checkCallStateInterval) {
          clearInterval(checkCallStateInterval);
        }
      };

      } catch (error) {
        if (!isProd) {
          console.error('❌ [WAVOIP] Erro ao inicializar webphone:', error);
        }
        if (onError) {
          onError(error);
        }
      }
    };

    // Executar inicialização
    initWebphone();

    // Cleanup ao desmontar
    return () => {
      try {
        if (!isProd) {
          console.log('🔴 [WAVOIP] Destruindo webphone');
        }
        WavoipWebphone.destroy();
      } catch (err) {
        if (!isProd) {
          console.error('❌ [WAVOIP] Erro ao destruir webphone:', err);
        }
      }
    };
  }, [token, onCallStart, onCallEnd, onError]);

  // Este componente não renderiza nada - o webphone é renderizado diretamente no DOM
  return null;
};

export default WavoipWidget;

/**
 * Funções utilitárias para controlar o webphone programaticamente
 */

/**
 * Formata número de telefone adicionando código do país se necessário
 * @param {string} phoneNumber - Número de telefone
 * @returns {string} Número formatado com código do país
 */
const formatPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  
  // Remove caracteres não numéricos
  let cleaned = phoneNumber.replace(/\D/g, '');
  
  // Se não começa com 55 (Brasil) e tem 10 ou 11 dígitos (DDD + número)
  if (!cleaned.startsWith('55') && (cleaned.length === 10 || cleaned.length === 11)) {
    cleaned = '55' + cleaned;
  }
  
  return cleaned;
};

/**
 * Abre o widget de telefone
 */
export const openWavoipWidget = () => {
  if (window.wavoip?.widget) {
    window.wavoip.widget.open();
  }
};

/**
 * Fecha o widget de telefone
 */
export const closeWavoipWidget = () => {
  if (window.wavoip?.widget) {
    window.wavoip.widget.close();
  }
};

/**
 * Alterna o widget (abre se fechado, fecha se aberto)
 */
export const toggleWavoipWidget = () => {
  if (window.wavoip?.widget) {
    window.wavoip.widget.toggle();
  }
};

/**
 * Define o número no input e abre o widget
 * @param {string} phoneNumber - Número de telefone (adiciona +55 automaticamente se necessário)
 */
export const callWithWavoip = (phoneNumber) => {
  if (window.wavoip?.call) {
    const formattedNumber = formatPhoneNumber(phoneNumber);
    window.wavoip.call.setInput(formattedNumber);
    window.wavoip.widget.open();
  }
};

/**
 * Inicia uma chamada diretamente
 * @param {string} phoneNumber - Número de telefone (adiciona +55 automaticamente se necessário)
 * @param {string[]} fromTokens - Tokens dos dispositivos (opcional)
 * @returns {Promise} Resultado da chamada
 */
export const startCall = async (phoneNumber, fromTokens = null) => {
  if (window.wavoip?.call) {
    const formattedNumber = formatPhoneNumber(phoneNumber);
    return await window.wavoip.call.startCall(formattedNumber, fromTokens);
  }
  return { err: { message: 'Wavoip não inicializado' } };
};

/**
 * Obtém a chamada ativa atual
 * @returns {Object|undefined} Dados da chamada ativa
 */
export const getActiveCall = () => {
  if (window.wavoip?.call) {
    return window.wavoip.call.getCallActive();
  }
  return undefined;
};

/**
 * Obtém a chamada de saída atual
 * @returns {Object|undefined} Dados da chamada de saída
 */
export const getOutgoingCall = () => {
  if (window.wavoip?.call) {
    return window.wavoip.call.getCallOutgoing();
  }
  return undefined;
};

/**
 * Obtém as ofertas de chamada recebidas
 * @returns {Array} Lista de ofertas
 */
export const getCallOffers = () => {
  if (window.wavoip?.call) {
    return window.wavoip.call.getOffers();
  }
  return [];
};
